import { RequestParameters } from "maplibre-gl";
import {
  PropsWithChildren,
  ReactElement,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocalStorage } from "@nens/react/hooks";
import { fetchWithError, fetchWithErrorBareResponse } from "@nens/react-query-utils";

import { Tenant, useTenants } from "../endpoints/tenants";
import { FetchTokenResponse, Scope, fetchToken } from "../endpoints/token";

interface LdoAuthProviderProps {
  loadingComponent: ReactElement;
  chooseTenantComponent: (tenants: Tenant[], setTenant: (tenant: Tenant) => void) => ReactElement;
  setTenantAndAuthToken: (tenantId: number, token: string | undefined) => void;
  alwaysChooseTenant?: boolean;
}

function createMapLibreTransformFunction(accessToken: string) {
  return (url: string): RequestParameters => {
    if (
      url.startsWith(`${window.location.protocol}//${window.location.host}`) ||
      url.startsWith("/")
    ) {
      return {
        url: url,
        headers: { Authorization: `Bearer ${accessToken}` },
      };
    } else return { url };
  };
}

export type ApiFetchFunction = (path: string, options?: RequestInit) => Promise<unknown>;

function createFetchFunction(accessToken: string): ApiFetchFunction {
  // Return a version of fetchWithError that always has a HTTP header
  // Authorization: Bearer <accessToken> added in.
  // No code outside of this module is passed the actual token, only this function,
  // so the token isn't visible to any other code.
  return function fetchWithErrorAndToken(
    path: string,
    options: {
      headers?: RequestInit["headers"];
      method?: RequestInit["method"];
      body?: RequestInit["body"];
    } = {}
  ) {
    const ourHeaders = {
      Authorization: `Bearer ${accessToken}`,
    };

    const ourOptions = { ...options };
    ourOptions.headers = { ...(options.headers ?? {}), ...ourHeaders };

    return fetchWithError<unknown>(path, ourOptions);
  };
}

export type ApiFetchFunctionBareResponse = (
  path: string,
  options?: RequestInit
) => Promise<Response>;

function createFetchFunctionBareResponse(accessToken: string): ApiFetchFunctionBareResponse {
  return function fetchWithErrorAndTokenBareResponse(
    path: string,
    options: {
      headers?: RequestInit["headers"];
      method?: RequestInit["method"];
      body?: RequestInit["body"];
    } = {}
  ) {
    const ourHeaders = {
      Authorization: `Bearer ${accessToken}`,
    };

    const ourOptions = { ...options };
    ourOptions.headers = { ...(options.headers ?? {}), ...ourHeaders };

    return fetchWithErrorBareResponse(path, ourOptions) as Promise<Response>;
  };
}

export const DEFAULT_CONTEXT_VALUE = {
  userId: -1,
  username: "",
  email: "",
  tenantId: -1,
  tenantName: "",
  apiFetchFunction: createFetchFunction(""),
  apiFetchFunctionBareResponse: createFetchFunctionBareResponse(""),
  mapLibreTransformFunction: createMapLibreTransformFunction(""),
  tenants: [] as Tenant[],
  setTenant: (_tenant: Tenant) =>
    console.log("This is here for the linter, if you see this then that's weird."),
  hasScope: (scope: Scope) => false,
};

export const LdoAuthContext = createContext(DEFAULT_CONTEXT_VALUE);

interface TokenStateForTenant {
  fetchTokenResponse: FetchTokenResponse | null;
}

export function LdoAuthProvider({
  loadingComponent,
  chooseTenantComponent,
  children,
  alwaysChooseTenant = false,
  setTenantAndAuthToken,
}: PropsWithChildren<LdoAuthProviderProps>) {
  const [tenantId, setTenantId] = useLocalStorage<number>("ldo_tenant_id", -1);
  const [tokensForTenants, setTokensForTenants] = useState<Record<number, TokenStateForTenant>>({});

  const tenantListResponse = useTenants();

  const updateTokenForTenant = useCallback(
    async (tenantId: number) => {
      if (tenantId !== -1) {
        setTokensForTenants((tokensForTenants) => {
          return { ...tokensForTenants, [tenantId]: { fetchTokenResponse: null } };
        });
        const fetchTokenResponse = await fetchToken(tenantId);
        setTokensForTenants((tokensForTenants) => {
          return {
            ...tokensForTenants,
            [tenantId]: {
              fetchTokenResponse,
            },
          };
        });
      }
    },
    [setTokensForTenants]
  );

  const setTenant = useCallback(
    (newTenantId: number) => {
      setTenantId(newTenantId);
      if (!tokensForTenants[newTenantId]) {
        updateTokenForTenant(newTenantId);
      }
    },
    [setTenantId, tokensForTenants, updateTokenForTenant]
  );

  if (!(tenantId in tokensForTenants)) {
    updateTokenForTenant(tenantId);
  }

  // Check every minute whether token should be updated
  const intervalRef = useRef(-1);
  useEffect(() => {
    const TOKEN_REFRESH_AGE_SECONDS = 3 * 60;
    if (intervalRef.current !== -1) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      if (!tenantId || !(tenantId in tokensForTenants)) return;
      const { fetchTokenResponse } = tokensForTenants[tenantId];
      if (!fetchTokenResponse) return;
      if (
        fetchTokenResponse.payload.exp - TOKEN_REFRESH_AGE_SECONDS <
        new Date().getTime() / 1000
      ) {
        fetchToken(tenantId).then((token) => {
          setTokensForTenants((tokensForTenants) => {
            return {
              ...tokensForTenants,
              [tenantId]: {
                fetchTokenResponse: token,
              },
            };
          });
        });
      }
    }, 60 * 1000) as unknown as number;
    return () => {
      if (intervalRef.current !== -1) {
        clearInterval(intervalRef.current);
      }
    };
  }, [tenantId, tokensForTenants]);

  const tokenForTenant = tokensForTenants[tenantId]?.fetchTokenResponse ?? null;
  setTenantAndAuthToken(tenantId, tokenForTenant?.accessToken ?? undefined);

  const contextValue = useMemo(() => {
    const hasScope = (scope: Scope) => {
      if (!tokenForTenant?.payload) return false;
      const scopes = tokenForTenant.payload.scope.split(" ");
      if (scopes.indexOf("superuser") !== -1) return true;
      return scopes.indexOf(scope) !== -1;
    };

    if (tokenForTenant) {
      return {
        userId: tokenForTenant.payload.user_id,
        username: tokenForTenant.payload.username,
        email: tokenForTenant.payload.email,
        tenantId: tokenForTenant.payload.tenant,
        tenantName: tokenForTenant.payload.tenant_name,
        apiFetchFunction: createFetchFunction(tokenForTenant.accessToken),
        apiFetchFunctionBareResponse: createFetchFunctionBareResponse(tokenForTenant.accessToken),
        mapLibreTransformFunction: createMapLibreTransformFunction(tokenForTenant.accessToken),
        tenants: tenantListResponse.isSuccess ? tenantListResponse.data : [],
        setTenant: (tenant: Tenant) => setTenant(tenant.id),
        hasScope,
      };
    } else {
      return DEFAULT_CONTEXT_VALUE;
    }
  }, [tokenForTenant, tenantListResponse, setTenant]);

  if (tenantListResponse.isError) {
    // Redirect to login
    window.location.href = "/auth/auth/login/?next=/";
    return null;
  }

  if (!tenantListResponse.isSuccess) {
    return loadingComponent;
  }

  const tenants: Tenant[] = tenantListResponse.data;

  if (tenants.length === 0) {
    // XXX
    return <p>User not authorized for any tenants.</p>;
  }

  if (!tenants.some((tenant) => tenant.id === tenantId)) {
    if (tenants.length === 1 && !alwaysChooseTenant) {
      // This should let the component rerender immediately
      setTenant(tenants[0].id);
      return loadingComponent;
    } else {
      return chooseTenantComponent(tenants, (tenant: Tenant) => setTenant(tenant.id));
    }
  }

  if (tokenForTenant === null) {
    return loadingComponent;
  }

  return <LdoAuthContext.Provider value={contextValue}>{children}</LdoAuthContext.Provider>;
}
